import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Instagram, Linkedin, Send } from 'lucide-react';
import { useState } from 'react';
import { navServices } from '../data/services';
import { useLanguage } from '../context/LanguageContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { lang, setLang, t } = useLanguage();
  const navigate = useNavigate();

  const handleServiceClick = (e, path) => {
    e.preventDefault();
    setServicesOpen(false);
    setIsOpen(false);
    
    if (path.includes('#')) {
      const [route, hash] = path.split('#');
      navigate(route);
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      navigate(path);
      // Scroll to top for non-hash routes
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const services = navServices;

  return (
    <nav className="fixed top-0 w-full bg-black z-50 border-b-0" data-testid="navbar">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex justify-between items-center h-16 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center md:ml-16" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_accretion-axiom/artifacts/3rwpkqcm_7a40f4_da18f8518e4c4d5dbeedf37582cf828c~mv2.avif"
              alt="Accretion Axiom Capital"
              className="h-8 md:h-12 w-auto"
              data-testid="logo-image"
            />
          </Link>
          
          {/* Socials + Lang + Hamburger - Solo móvil, alineado derecha */}
          <div className="flex items-center gap-4 lg:hidden">
            <div className="flex items-center gap-3">
              <a href="https://t.me/+gbLD_8i3AbIzMTY0" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                <Send size={14} />
              </a>
              <a href="https://www.linkedin.com/company/accretion-axiom-capital/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                <Linkedin size={14} />
              </a>
              <a href="https://www.instagram.com/accretion.capital/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                <Instagram size={14} />
              </a>
              <div className="flex gap-0.5 ml-1 bg-gray-800 rounded-full p-0.5">
                <button 
                  onClick={() => setLang('en')}
                  className={`px-2 py-1 text-[10px] rounded-full transition-all flex items-center gap-1 ${lang === 'en' ? 'bg-white text-black font-bold' : 'text-gray-400 hover:text-white'}`}
                >
                  <img src="https://flagcdn.com/w20/gb.png" alt="EN" className="w-3 h-2 object-cover rounded-sm" />
                  EN
                </button>
                <button 
                  onClick={() => setLang('es')}
                  className={`px-2 py-1 text-[10px] rounded-full transition-all flex items-center gap-1 ${lang === 'es' ? 'bg-white text-black font-bold' : 'text-gray-400 hover:text-white'}`}
                >
                  <img src="https://flagcdn.com/w20/es.png" alt="ES" className="w-3 h-2 object-cover rounded-sm" />
                  ES
                </button>
              </div>
            </div>
            
            {/* Hamburger button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white p-2"
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-between flex-1 ml-16">
            {/* Left Side Nav */}
            <div className="flex items-center space-x-8">
              <Link
                to="/realestate"
                className="text-white hover:text-gray-300 transition-colors duration-200 font-medium text-sm tracking-wide"
                data-testid="nav-real-estate"
              >
                REAL ESTATE FUND & DEALS
              </Link>
              
              <Link
                to="/quantitative"
                className="text-white hover:text-gray-300 transition-colors duration-200 font-medium text-sm tracking-wide"
                data-testid="nav-quantitative"
              >
                QUANTITATIVE FUND
              </Link>

              {/* Services Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className="text-white hover:text-gray-300 transition-colors duration-200 font-medium text-sm tracking-wide flex items-center py-4"
                  data-testid="nav-services"
                >
                  {t('nav.premiumServices')}
                  <ChevronDown size={16} className="ml-1" />
                </button>
                
                {servicesOpen && (
                  <div 
                    className="absolute top-full left-0 w-80 bg-black border border-gray-700 rounded-lg shadow-2xl py-2"
                  >
                    {services.map((service, idx) => (
                      <a
                        key={service.name}
                        href={service.path}
                        onClick={(e) => handleServiceClick(e, service.path)}
                        className={`block px-6 py-3 hover:bg-gray-900 transition-colors duration-150 cursor-pointer ${idx === 0 ? 'border-b border-gray-800 py-4' : ''}`}
                        data-testid={`service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className={`font-semibold mb-1 flex items-center gap-2 ${idx === 0 ? 'text-white text-lg' : 'text-gray-300 text-sm'}`}>
                          {idx > 0 && <span className="text-purple-400 text-xs">•</span>}
                          {service.name}
                        </div>
                        <div className={`text-xs ${idx === 0 ? 'text-gray-400' : 'text-gray-500'}`}>{service.description}</div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Side Nav */}
            <div className="flex items-center space-x-6">
              <Link
                to="/education"
                className="text-white hover:text-gray-300 transition-colors duration-200 font-medium text-sm tracking-wide"
                data-testid="nav-education"
              >
                FREE BLOCKCHAIN EDUCATION
              </Link>
              
              <Link
                to="/about"
                className="text-white hover:text-gray-300 transition-colors duration-200 font-medium text-sm tracking-wide"
                data-testid="nav-about"
              >
                ABOUT
              </Link>

              <Link
                to="/#contact"
                className="text-white hover:text-gray-300 transition-colors duration-200 font-medium text-sm tracking-wide"
                data-testid="nav-contact"
              >
                CONTACT
              </Link>

              {/* Social Icons */}
              <div className="flex items-center space-x-2">
                <a href="https://t.me/+gbLD_8i3AbIzMTY0" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                  <Send size={16} />
                </a>
                <a href="https://www.linkedin.com/company/accretion-axiom-capital/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                  <Linkedin size={16} />
                </a>
                <a href="https://www.instagram.com/accretion.capital/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                  <Instagram size={16} />
                </a>
              </div>

              {/* Language Selector with Flags */}
              <div className="flex items-center border border-gray-700 rounded overflow-hidden">
                <button
                  onClick={() => setLang('en')}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${lang === 'en' ? 'bg-white text-black' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
                >
                  <img src="https://flagcdn.com/w20/gb.png" alt="EN" className="w-4 h-3 object-cover" />
                  EN
                </button>
                <button
                  onClick={() => setLang('es')}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${lang === 'es' ? 'bg-white text-black' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
                >
                  <img src="https://flagcdn.com/w20/es.png" alt="ES" className="w-4 h-3 object-cover" />
                  ES
                </button>
              </div>

              <Link
                to="/login"
                className="bg-white text-black px-5 py-2 rounded text-sm font-semibold hover:bg-gray-200 transition-all duration-200"
                data-testid="nav-login"
              >
                LOGIN
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="lg:hidden bg-black" data-testid="mobile-menu">
          <div className="px-4 pt-4 pb-4">
            {/* Pages list */}
            <div className="space-y-2">
              {/* Páginas principales - todas iguales, grandes y en negrita */}
              <Link
                to="/realestate"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-3 text-white text-base font-bold hover:bg-gray-900 rounded transition-colors duration-200"
                data-testid="mobile-nav-real-estate"
              >
                AAC Real Estate & Deals
              </Link>
              <Link
                to="/quantitative"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-3 text-white text-base font-bold hover:bg-gray-900 rounded transition-colors duration-200"
                data-testid="mobile-nav-quantitative"
              >
                AAC Quantitative DAO
              </Link>
              <Link
                to="/all-services"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-3 text-white text-base font-bold hover:bg-gray-900 rounded transition-colors duration-200"
                data-testid="mobile-nav-all-services"
              >
                All Services Overview
              </Link>
              
              {/* Filtrar "All Services Overview" del dropdown ya que está arriba */}
              {services.filter(s => s.name !== 'All Services Overview').map((service) => (
                <a
                  key={service.name}
                  href={service.path}
                  onClick={(e) => handleServiceClick(e, service.path)}
                  className="block px-3 py-2 text-gray-400 hover:bg-gray-900 rounded transition-colors duration-200 text-sm cursor-pointer"
                  data-testid={`mobile-service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {service.name}
                </a>
              ))}
              
              <div className="border-t border-gray-800 my-2"></div>
              
              <Link
                to="/education"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-2 text-gray-300 hover:bg-gray-900 rounded transition-colors duration-200"
                data-testid="mobile-nav-education"
              >
                Free Blockchain Education
              </Link>
              <Link
                to="/about"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-2 text-gray-300 hover:bg-gray-900 rounded transition-colors duration-200"
                data-testid="mobile-nav-about"
              >
                About
              </Link>
              <Link
                to="/#contact"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-2 text-gray-300 hover:bg-gray-900 rounded transition-colors duration-200"
                data-testid="mobile-nav-contact"
              >
                Contact
              </Link>
              <Link
                to="/login"
                onClick={() => setIsOpen(false)}
                className="block bg-white text-black px-3 py-2 rounded hover:bg-gray-200 transition-all duration-200 text-center font-semibold"
                data-testid="mobile-nav-login"
              >
                Login
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;